// The catalog of quick-link cards the Work dashboard can show.
//
// DECIDED 2026-07-26 (Anthony): the dashboard grid is user-customizable, not
// merely role-correct. Role-correctness already shipped in E3b — WorkHome picks
// EmployeeDashboard for admins and TeacherDashboard for teachers, and the
// teacher side is deny-by-default per permission — so there was nothing left for
// role to fix. And it could not have helped anyway: the live population of this
// surface is anthony@ and grace@, both admin+owner with identical reach. Role
// has no discriminating power over two users with the same permissions; only a
// per-user preference can tell them apart, which is the point, because they do
// different jobs.
//
// THE CATALOG IS DERIVED FROM portalNav.js, NOT HAND-WRITTEN. The sidebar config
// already knows every destination, its label, its icon, its route and its
// gating (requiresPerm / adminOnly). Re-listing them here would create a second
// list to keep in sync, and the first thing to rot would be a card pointing at a
// route the sidebar had already dropped. Deriving means a removed nav item
// leaves the picker for free.
//
// What portalNav does NOT carry is presentation — a card has a one-line
// description and an accent, a nav row has neither. That is what PRESENTATION
// below adds, keyed by route, with a graceful fallback so a nav item added
// tomorrow shows up as a usable (if terser) card rather than breaking.

import { PORTAL_NAV_GROUPS, PORTAL_ADMIN_GROUP } from './portalNav';
import { hasPerm } from '../../utils/permissions';

// Per-route presentation. Descriptions for the six that shipped hardcoded are
// carried over VERBATIM so the default view is byte-identical to what Anthony
// and Grace see today; the rest were written to match their voice.
const PRESENTATION = {
	'/portal/work/today': {
		description: "Today's lessons, announcements, and start teaching.",
		accent: 'var(--grad-summer)',
	},
	'/portal/work/students': {
		description: 'Your student roster and individual profiles.',
		accent: 'var(--grad-lessons)',
	},
	'/portal/work/scheduling': {
		description: 'Calendar, slot editor, and the visual grid.',
		accent: 'var(--grad-custom)',
	},
	'/portal/work/inquiries': {
		description: 'Review new-student registrations and export to MMS.',
		accent: 'var(--grad-littles)',
	},
	'/portal/work/docs': {
		description: 'Scripts, show flows, and working pages.',
		accent: 'var(--grad-brand)',
	},
	'/portal/work/tasks': {
		description: 'The work queue — what needs doing, and what is parked.',
		accent: 'var(--grad-littles)',
	},
	'/portal/work/teacher-scheduling': {
		description: 'Teacher rotation, time off, and scheduling rules.',
	},
	'/portal/work/lessons-taught': {
		description: 'Attended lessons per teacher, per month, for pay.',
	},
	'/portal/work/manage-lessons': {
		description: 'Create, edit, and reschedule individual lessons.',
	},
	'/portal/work/recital-planning': {
		description: 'Build the set list and place songs on rehearsals.',
	},
	'/portal/work/workshop-enrollments': {
		description: 'Who has signed up for each workshop.',
	},
	'/portal/work/lesson-packs': {
		description: 'Voice pack purchases and their remaining lessons.',
	},
	'/portal/work/announcements': {
		description: 'Compose and send an email announcement.',
	},
	'/portal/work/email-families': {
		description: 'Write to a family from the studio address.',
	},
	'/portal/work/email-templates': {
		description: 'The reusable email bodies.',
	},
	'/portal/work/manage-announcements': {
		description: 'The banner families see when they sign in.',
	},
	'/portal/work/notes': {
		description: 'Lesson Recaps, by student and by date.',
	},
	'/portal/work/recital-songs': {
		description: 'Song assignments and the recital set.',
	},
	'/portal/work/requests': {
		description: 'Sheet music and material requests from teachers.',
	},
	'/portal/work/import': { description: 'Bring students in from a CSV.' },
	'/portal/work/import-lessons': { description: 'Bring lessons in from a CSV.' },
	'/portal/work/link-lessons': {
		description: 'Attach imported lessons to student records.',
	},
	'/portal/work/setup-student-types': {
		description: 'The student type list used across the portal.',
	},
	'/portal/work/admin': {
		description: 'Manage users, roles, and system settings.',
		accent: 'var(--grad-brand)',
	},
};

// --grad-quiet is deliberately not in the rotation: it is the "opens later"
// muted token, and a card wearing it reads as disabled. Cycling the other five
// by catalog position keeps a card that has no explicit accent on the palette
// instead of inventing a color, and keeps it stable between renders.
const ACCENT_CYCLE = [
	'var(--grad-summer)',
	'var(--grad-lessons)',
	'var(--grad-custom)',
	'var(--grad-littles)',
	'var(--grad-brand)',
];

// Flatten portalNav into the card catalog. `group` carries the sidebar's own
// section title through so the picker can show the same shape the nav does —
// twenty-two flat checkboxes would be a wall.
function buildCatalog() {
	const out = [];
	PORTAL_NAV_GROUPS.forEach((g) => {
		g.items.forEach((item) => {
			out.push({ ...item, group: g.title });
		});
	});
	PORTAL_ADMIN_GROUP.items.forEach((item) => {
		out.push({ ...item, group: PORTAL_ADMIN_GROUP.title, adminOnly: true });
	});
	return out.map((item, i) => {
		const p = PRESENTATION[item.to] || {};
		return {
			// The ROUTE is the key, not the label: a label is copy and gets
			// reworded (Notes -> Lesson Recaps, 2026-07-24), which would silently
			// drop the card from everyone's saved selection. A route is identity.
			key: item.to,
			label: item.label,
			icon: item.icon,
			to: item.to,
			group: item.group,
			requiresPerm: item.requiresPerm,
			adminOnly: item.adminOnly === true,
			description: p.description || '',
			accent: p.accent || ACCENT_CYCLE[i % ACCENT_CYCLE.length],
		};
	});
}

export const DASHBOARD_CARDS = buildCatalog();

// What a user who has never chosen sees: exactly the six that were hardcoded in
// EmployeeDashboard before this shipped, in their original order. Changing this
// list changes the dashboard for everyone who has not customized it.
export const DEFAULT_DASHBOARD_CARDS = [
	'/portal/work/today',
	'/portal/work/students',
	'/portal/work/scheduling',
	'/portal/work/inquiries',
	'/portal/work/docs',
	'/portal/work/tasks',
];

const BY_KEY = Object.fromEntries(DASHBOARD_CARDS.map((c) => [c.key, c]));

export function dashboardCard(key) {
	return BY_KEY[key] || null;
}

// The catalog this viewer may actually choose from. Admins bypass hasPerm (it
// short-circuits on role/owner), so today this filters nothing for Anthony or
// Grace — it is here so the surface stays correct if a non-admin ever reaches
// it, rather than offering a card whose route the rules would deny.
export function catalogFor(userProfile) {
	const isAdmin = userProfile?.role === 'admin';
	return DASHBOARD_CARDS.filter((c) => {
		if (c.adminOnly && !isAdmin) return false;
		if (c.requiresPerm && !hasPerm(userProfile, c.requiresPerm)) return false;
		return true;
	});
}

// Normalize whatever came back from Firestore into a usable selection.
//
// The absent-vs-empty distinction is the same one cardProperties.normalizeProperties
// earned on the board, and for the same reason: a doc that has never been written
// must not be read as "the user turned everything off", or it becomes impossible
// to HAVE an empty dashboard — the defaults would keep coming back.
//   • null / undefined / not-an-array -> the defaults (never chosen)
//   • an array -> kept, minus anything this build no longer offers
// An EMPTY array is a legitimate choice (no cards, just the welcome) and survives.
//
// `catalog` is passed in rather than read from the module so the filter also
// drops a card the VIEWER may not have, not merely one the build has retired.
export function normalizeDashboardCards(stored, catalog = DASHBOARD_CARDS) {
	const allowed = new Set(catalog.map((c) => c.key));
	if (!Array.isArray(stored))
		return DEFAULT_DASHBOARD_CARDS.filter((k) => allowed.has(k));
	const seen = new Set();
	return stored.filter((k) => {
		if (!allowed.has(k) || seen.has(k)) return false;
		seen.add(k);
		return true;
	});
}

// The selection in CATALOG order, so the grid keeps the sidebar's reading order
// no matter what order the boxes were ticked in. This is the order for anyone
// who has never ARRANGED their cards — see orderedDashboardCards below.
export function visibleDashboardCards(selected, catalog = DASHBOARD_CARDS) {
	const chosen = new Set(selected);
	return catalog.filter((c) => chosen.has(c.key));
}

// REORDER (Anthony, 2026-10-10: "give me the ability to reorder the cards").
//
// The saved `cards` array has always been ordered, but until now nothing read
// its order: the grid sorted by catalog, so the array sits in TICK order, which
// nobody chose. Honouring it as-is would rearrange everyone's dashboard on the
// next load for no reason they could see. So the saved order governs only once
// a person has actually arranged (`ordered: true`, written with the cards).
// Until then: catalog order, exactly as before.
//
// `ordered` must be written TOGETHER with `cards` every time:
// saveMyPrefs merges the TOP-LEVEL `dashboard` key, so a write of
// `{ cards }` alone replaces the map and silently drops the flag (CLAUDE.md,
// "{ merge: true } IS NOT DEEP").
export function orderedDashboardCards(selected, catalog = DASHBOARD_CARDS, ordered = false) {
	if (!ordered) return visibleDashboardCards(selected, catalog);
	const byKey = new Map(catalog.map((c) => [c.key, c]));
	return selected.map((k) => byKey.get(k)).filter(Boolean);
}

// `keys` with `key` moved to `toIndex` (clamped). Pure; returns a new array,
// and the SAME array when nothing moves, so a caller can compare by identity.
export function moveCardKey(keys, key, toIndex) {
	const from = keys.indexOf(key);
	if (from === -1) return keys;
	const to = Math.max(0, Math.min(keys.length - 1, toIndex));
	if (to === from) return keys;
	const next = keys.filter((k) => k !== key);
	next.splice(to, 0, key);
	return next;
}
